import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import TypeBadge from "../../components/ui/TypeBadge.jsx";
import { Upload, PieChart, TrendingUp, LineChart } from "lucide-react";
import { getProductSummaryByType } from "../../api/lenderPortal/mortgageSummary";
import {
  getMortgageProducts,
  getInvoiceFinanceProducts,
} from "../../api/lenderPortal/listedproducts";

const CURRENT_LENDER_NAME =
  (typeof window !== "undefined" && window.AUTH_LENDER_NAME) || "";

// ✅ adjust this if your route path is different
const LISTED_PRODUCTS_ROUTE = "/lender/listed";

const PRODUCT_TYPE_OPTIONS = [
  { value: "btl", label: "Buy-to-Let" },
  { value: "if", label: "Invoice Finance" },
];

const RECENT_PRODUCT_OPTIONS = PRODUCT_TYPE_OPTIONS;

function productSortDate(row) {
  return new Date(
    row?._raw?.creation ||
      row?._raw?.modified ||
      row?._raw?.creation_date ||
      0
  ).getTime();
}

function tagProductLine(rows, line) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    _productLine: line,
  }));
}


/* ---------- Live delta for KPIs (same as before) ---------- */
function useLiveDelta() {
  const [delta, setDelta] = useState(
    () => Number((Math.random() * 2 - 1).toFixed(1))
  );

  useEffect(() => {
    const id = setInterval(
      () => setDelta(Number((Math.random() * 6 - 3).toFixed(1))),
      5000
    );
    return () => clearInterval(id);
  }, []);

  return delta;
}

/* ---------- KPI card with hover + smooth loading skeleton ---------- */
const Kpi = ({ title, value, icon: Icon, trend, loading }) => {
  const liveDelta = useLiveDelta();
  const hasTrend = typeof trend === "string" && trend.trim().length > 0;

  let arrow;
  let pct;
  let positive;

  if (hasTrend) {
    const trimmed = trend.trim();
    arrow = trimmed.charAt(0);
    pct = trimmed.slice(1).trim();
    positive = arrow === "▲" || arrow === "+";
  } else {
    const delta = liveDelta;
    positive = delta >= 0;
    arrow = positive ? "▲" : "▼";
    pct = `${Math.abs(delta).toFixed(1)}%`;
  }

  return (
    <Card
      className={`
        p-3 sm:p-4
        transition-all duration-300
        transform
        ${loading ? "opacity-70 translate-y-1" : "opacity-100 hover:-translate-y-1 hover:shadow-lg"}
      `}
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl grid place-items-center bg-slate-100 border border-slate-200">
          {Icon ? <Icon className="w-5 h-5 text-slate-700" /> : null}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-slate-500">{title}</div>

          {/* Value + trend skeleton vs real data */}
          {loading ? (
            <>
              <div className="mt-1 h-6 w-24 bg-sky-100/80 rounded animate-pulse" />
              <div className="mt-1 h-3 w-16 bg-sky-50 rounded animate-pulse" />
            </>
          ) : (
            <>
              <div className="text-2xl font-extrabold text-slate-900 tracking-tight truncate">
                {value}
              </div>
              <div
                className={`text-xs mt-0.5 ${positive ? "text-emerald-600" : "text-red-600"
                  } flex items-center gap-1`}
                aria-live="polite"
              >
                <span>{arrow}</span>
                <span>{pct}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const StatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const canonical =
    s === "active" || s === "live" || s === "approved" || s === "activate"
      ? "active"
      : s === "scheduled" || s === "schedule"
        ? "scheduled"
        : s === "deactivate" || s === "deactivated" || s === "inactive"
          ? "deactivated"
          : s;

  const classes =
    canonical === "active"
      ? "bg-emerald-100 text-emerald-800"
      : canonical === "scheduled"
        ? "bg-amber-100 text-amber-800"
        : "bg-rose-100 text-rose-800";

  const label =
    canonical === "active"
      ? "Active"
      : canonical === "scheduled"
        ? "Scheduled"
        : canonical === "deactivated"
          ? "Deactivated"
          : status;

  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold text-center ${classes}`}
    >
      {label}
    </span>
  );
};

function Overview() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedType, setSelectedType] = useState("btl");
  const [recentType, setRecentType] = useState(null); // resolved after first load
  const [productsLoading, setProductsLoading] = useState(true);
  const recentLoadIdRef = React.useRef(0);

  // rowsToShow for Recently Added
  const [rowsToShow, setRowsToShow] = useState(5);

  const loadRecentProducts = useCallback(async (preferred = "btl", { autoSelect = false } = {}) => {
    const loadId = ++recentLoadIdRef.current;
    setProductsLoading(true);
    setRowsToShow(5);

    try {
      const [btlRaw, ifRaw] = await Promise.all([
        getMortgageProducts().catch(() => []),
        getInvoiceFinanceProducts().catch(() => []),
      ]);
      if (loadId !== recentLoadIdRef.current) return;

      const btlRows = tagProductLine(btlRaw, "btl");
      const ifRows = tagProductLine(ifRaw, "if");
      const hasBtl = btlRows.length > 0;
      const hasIf = ifRows.length > 0;

      // Only auto-pick a type that has products on the initial load.
      // Manual dropdown changes always respect the user's selection.
      let type = preferred;
      if (autoSelect) {
        if (preferred === "btl" && !hasBtl && hasIf) {
          type = "if";
        } else if (preferred === "if" && !hasIf && hasBtl) {
          type = "btl";
        } else if (!hasBtl && !hasIf) {
          type = "btl";
        }
      }

      const rows = type === "if" ? ifRows : btlRows;

      setRecentType(type);
      setProducts(rows);
    } catch (e) {
      if (loadId !== recentLoadIdRef.current) return;
      console.error("Failed to load overview products:", e);
      const isAuthError =
        e?.response?.status === 401 || e?.response?.status === 403;
      if (!isAuthError) {
        setError(
          "Unable to load some live data. Table might be partial or empty."
        );
      }
      setRecentType(preferred || "btl");
      setProducts([]);
    } finally {
      if (loadId === recentLoadIdRef.current) {
        setProductsLoading(false);
      }
    }
  }, []);

  // Initial load — auto-select a type with products (prefer BTL)
  useEffect(() => {
    loadRecentProducts("btl", { autoSelect: true });
  }, [loadRecentProducts]);

  // Product summary KPIs — refetch when product type dropdown changes
  useEffect(() => {
    let cancelled = false;

    async function loadSummary() {
      setSummaryLoading(true);
      try {
        const summaryData = await getProductSummaryByType(selectedType);
        if (!cancelled) setSummary(summaryData || null);
      } catch (e) {
        console.error("Failed to load product summary:", e);
        if (!cancelled) {
          const isAuthError =
            e?.response?.status === 401 || e?.response?.status === 403;
          if (!isAuthError) {
            setError((prev) => prev || "Unable to load product summary KPIs.");
          }
          setSummary(null);
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    }

    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedType]);

  const selectedTypeLabel =
    PRODUCT_TYPE_OPTIONS.find((o) => o.value === selectedType)?.label ||
    "Buy-to-Let";

  const recentTypeLabel =
    RECENT_PRODUCT_OPTIONS.find((o) => o.value === recentType)?.label ||
    "Buy-to-Let";

  // Most recent products for the selected recent-type
  const recentProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    const sorted = [...products].sort(
      (a, b) => productSortDate(b) - productSortDate(a)
    );

    return sorted.slice(0, 15);
  }, [products]);

  const visibleProducts = useMemo(
    () => recentProducts.slice(0, rowsToShow),
    [recentProducts, rowsToShow]
  );

  const showIfColumns = recentType === "if";
  const recentColSpan = 7;


  const kpiTotalProducts = summary?.total_products ?? 0;
  const kpiActiveProducts = summary?.active_products ?? 0;

  const kpiAvgDealSize = useMemo(() => {
    const v = summary?.average_deal_size?.value;
    if (v == null) return "£0";
    return Number(v).toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    });
  }, [summary]);

  const kpiConversionRate = summary?.conversion_rate?.value ?? "0%";

  const computedTopLenders = useMemo(() => {
    const top10 = summary?.lender_ranking?.top_10;

    if (Array.isArray(top10) && top10.length) {
      const norm = (s) => String(s || "").trim().toLowerCase();
      const me = norm(CURRENT_LENDER_NAME);

      return top10.map((item, idx) => {
        const rawName =
          item.lender_name ||
          item.lender ||
          item.name ||
          item.Lender ||
          `Lender ${idx + 1}`;
        const isCurrent = me && norm(rawName) === me;
        const anonymizedName = `Lender ${idx + 1}`;

        return {
          rank: item.rank ? item.rank.replace("#", "") : String(idx + 1),
          name: anonymizedName,
          rawName,
          isCurrent,
          conversionRate:
            item.conversion_rate ??
            (item.product_count != null
              ? `${item.product_count} products`
              : undefined),
          trend: item.trend,
        };
      });
    }

    // No backend ranking data – don't show dummy lenders
    return [];
  }, [summary]);

  const handleShowMore = () => {
    setRowsToShow((prev) =>
      Math.min(prev + 5, 15, recentProducts.length || 5)
    );
  };

  return (
    <>
      {/* HERO with product dropdown on right */}
      <div className="rounded-2xl bg-sky-400 text-white p-4 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold">Welcome back!</h2>
          <p className="text-sm md:text-base opacity-90 mt-1">
            Upload all products in one place and stay updated with analytics.
          </p>
          {error && (
            <p className="mt-2 text-xs bg-red-50 text-red-700 px-3 py-1 rounded">
              {error}
            </p>
          )}
        </div>

        <label className="flex w-full flex-col gap-1.5 sm:w-auto sm:flex-row sm:items-center sm:gap-2 md:shrink-0">
          <span className="text-sm/none font-medium opacity-90">Product:</span>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full max-w-full text-slate-800 bg-white rounded-md px-3 py-2.5 border border-sky-200 shadow-sm focus:outline-none sm:w-auto sm:min-w-[12rem]"
          >
            {PRODUCT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* KPIs with smooth loading */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <Kpi
          title="Total Products"
          value={String(kpiTotalProducts)}
          icon={Upload}
          loading={summaryLoading}
        />
        <Kpi
          title="Active Products"
          value={String(kpiActiveProducts)}
          icon={PieChart}
          loading={summaryLoading}
        />
        <Kpi
          title="Average Deal Size"
          value={kpiAvgDealSize}
          icon={TrendingUp}
          trend={summary?.average_deal_size?.trend}
          loading={summaryLoading}
        />
        <Kpi
          title="Conversion Rate"
          value={kpiConversionRate}
          icon={LineChart}
          trend={summary?.conversion_rate?.trend}
          loading={summaryLoading}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Recently Added Products */}
        <Card className="p-4 sm:p-5">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-bold">Recently Added Products</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {productsLoading || !recentType
                  ? "Loading recent products…"
                  : `Showing most recent ${recentTypeLabel} products`}
              </p>
            </div>
            <label className="flex w-full flex-col gap-1 sm:w-auto sm:flex-row sm:items-center sm:gap-2 shrink-0">
              <span className="text-xs font-medium text-slate-600">Type:</span>
              <select
                value={recentType || "btl"}
                onChange={(e) => loadRecentProducts(e.target.value)}
                disabled={productsLoading}
                aria-label="Recently added product type"
                className="w-full max-w-full text-slate-800 bg-white rounded-md px-3 py-2 border border-slate-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:opacity-60 sm:w-auto sm:min-w-[10rem] text-sm"
              >
                {RECENT_PRODUCT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="-mx-1 overflow-x-auto px-1 sm:mx-0 sm:px-0">
            {productsLoading || !recentType ? (
              <div className="space-y-2 py-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="h-10 w-full bg-sky-50 rounded animate-pulse"
                  />
                ))}
              </div>
            ) : (
            <table className="min-w-[640px] w-full text-xs sm:min-w-full sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  {showIfColumns ? (
                    <>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        SECTOR
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        IP
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        SERVICE FEE
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        DISCOUNT RATE
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        TERM
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap sm:px-4 sm:py-3">
                        AMOUNT RANGE
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        STATUS
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        PRODUCT
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        CATEGORY
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        RATE
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold whitespace-nowrap sm:px-4 sm:py-3">
                        ARRANGEMENT FEE
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        TERM
                      </th>
                      <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap sm:px-4 sm:py-3">
                        AMOUNT RANGE
                      </th>
                      <th className="text-center px-2 py-2.5 font-semibold sm:px-4 sm:py-3">
                        STATUS
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleProducts.map((p, i) => {
                    const line = p._productLine || recentType;
                    const isIf = line === "if";
                    const productName = p.product || `Product ${i + 1}`;
                    const category =
                      p.category ||
                      p._raw?.product_category ||
                      p._raw?.category ||
                      (isIf ? "IF" : "BTL");
                    const rate = p.rate ?? "—";
                    const term = p.term ?? "—";
                    const range = p.range ?? "—";
                    const status = p.status ?? "Active";
                    const arrangementFee = p.arrangementFee ?? "—";

                    const handleClick = () => {
                      const q = encodeURIComponent(
                        isIf ? p.sector || productName : productName
                      );
                      const lineParam = isIf ? "if" : "btl";
                      navigate(
                        `${LISTED_PRODUCTS_ROUTE}?line=${lineParam}&q=${q}`
                      );
                    };

                    return (
                      <tr
                        key={`${line}-${p._id || productName}-${i}`}
                        className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={handleClick}
                      >
                        {showIfColumns ? (
                          <>
                            <td className="px-2 py-2.5 font-medium text-gray-900 sm:px-4 sm:py-3 max-w-[200px] sm:max-w-none">
                              <span className="line-clamp-2 sm:line-clamp-none">
                                {p.sector || productName}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-center tabular-nums sm:px-4 sm:py-3">
                              {p.ip ?? "—"}
                            </td>
                            <td className="px-2 py-2.5 text-center tabular-nums sm:px-4 sm:py-3">
                              {p.serviceFee ?? "—"}
                            </td>
                            <td className="px-2 py-2.5 text-center tabular-nums sm:px-4 sm:py-3">
                              {p.discountRate ?? "—"}
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">{term}</td>
                            <td className="px-2 py-2.5 whitespace-nowrap sm:px-4 sm:py-3">
                              {range}
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">
                              <div className="flex justify-center">
                                <StatusBadge status={status} />
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-2 py-2.5 font-medium text-gray-900 sm:px-4 sm:py-3 max-w-[200px] sm:max-w-none">
                              <span className="line-clamp-2 sm:line-clamp-none">
                                {productName}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">
                              <TypeBadge t={category} />
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">{rate}</td>
                            <td className="px-2 py-2.5 text-center tabular-nums sm:px-4 sm:py-3">
                              {arrangementFee}
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">{term}</td>
                            <td className="px-2 py-2.5 whitespace-nowrap sm:px-4 sm:py-3">
                              {range}
                            </td>
                            <td className="px-2 py-2.5 sm:px-4 sm:py-3">
                              <div className="flex justify-center">
                                <StatusBadge status={status} />
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}

                {recentProducts.length === 0 && (
                  <tr>
                    <td
                      className="px-2 py-6 text-center text-gray-500 sm:px-4"
                      colSpan={recentColSpan}
                    >
                      No {recentTypeLabel.toLowerCase()} products to display.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>

          {!productsLoading &&
            recentType &&
            recentProducts.length > 5 &&
            rowsToShow < Math.min(15, recentProducts.length) && (
              <div className="mt-3 flex justify-stretch sm:justify-end">
                <button
                  type="button"
                  onClick={handleShowMore}
                  className="w-full text-xs px-3 py-2 rounded-full border border-sky-200 text-sky-600 hover:bg-sky-50 transition-colors sm:w-auto sm:py-1.5"
                >
                  Show more
                </button>
              </div>
            )}
        </Card>

        {/* Top Lenders with smooth loading */}
        <Card className="p-4 sm:p-5">
          <h3 className="text-lg font-bold mb-4 break-words">
            Top Lenders — {selectedTypeLabel}
          </h3>

          <ol className="space-y-3">
            {summaryLoading
              ? Array.from({ length: 4 }).map((_, idx) => (
                <li
                  key={`tl-skeleton-${idx}`}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="h-4 w-6 bg-sky-50 rounded animate-pulse" />
                    <div className="h-6 w-6 rounded-full bg-sky-50 animate-pulse" />
                    <div className="h-4 w-32 bg-sky-50 rounded animate-pulse" />
                  </div>
                  <div className="h-3 w-20 bg-sky-50 rounded animate-pulse" />
                </li>
              ))
              : computedTopLenders.map((l) => (
                <li
                  key={l.rank + l.name}
                  className={`flex flex-col gap-2 border-b border-gray-100 pb-3 last:border-0 last:pb-0 transition-opacity duration-300 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${l.isCurrent ? "bg-sky-50/60 rounded-lg px-2 -mx-2" : ""}`}
                >
                  <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                    <span className="shrink-0 text-gray-500 font-semibold">
                      #{l.rank}
                    </span>
                    <span className={`min-w-0 font-semibold break-words ${l.isCurrent ? "text-sky-700" : "text-gray-900"}`}>
                      {l.name}
                      {l.isCurrent ? " (You)" : ""}
                    </span>
                  </div>
                  {(l.conversionRate || l.trend) && (
                    <div className="flex flex-col text-xs text-gray-500 sm:items-end sm:shrink-0">
                      {l.conversionRate && (
                        <span>{l.conversionRate} conv.</span>
                      )}
                      {l.trend && <span>{l.trend}</span>}
                    </div>
                  )}
                </li>
              ))}

            {!summaryLoading && computedTopLenders.length === 0 && (
              <li className="text-sm text-gray-500">No lenders to display.</li>
            )}
          </ol>
        </Card>
      </div>
    </>
  );
}

export default Overview;

